import React, { useContext } from 'react'
import { Modal, Spinner, Text, View } from 'oio-react'
import Helmet from 'react-helmet'
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom'
import ListMenuButton from 'src/apps/Global/components/ListMenuButton'
import InitiativeJoinButton from 'src/apps/Initiative/components/JoinButton'
import InitiativeMoreButton from 'src/apps/Initiative/components/MoreButton'
import ThreadChatView from 'src/apps/Messages/apps/ThreadChatView'
import ModalRoute from 'src/components/utils/ModalRoute'
import RichText from 'src/components/utils/RichText'
import { useOrganization } from 'src/graphql/hooks'
import { InitiativeHierarchyContext, withInitiativeHierarchyProvider }
   from 'src/libraries/contexts/InitiativeHierarchy'
import ProtoThemeContext from 'src/libraries/contexts/ProtoTheme'
import Settings from '~/apps/Settings'
import PeopleBlock from '~/components/PeopleBlock'
import People from './People'
import Related from './Related'

const GroupView = () => {
   const match = useRouteMatch()
   const { protoTheme: theme } = useContext(ProtoThemeContext)

   const buttonStyleProps = {
      activeBackgroundColor: theme.tmpAsideButtonActiveBackgroundColor,
      activeTextColor: theme.tmpAsideButtonActiveTextColor,
      height: theme.tmpSecondaryButtonHeight,
      textColor: theme.tmpButtonTextColor,
      textSize: theme.tmpSecondaryButtonTextSize
   }

   const { organization } = useOrganization()
   const { highlightColor: buttonColor } = organization.theme

   const { initiative, initiativeLoading } = useContext(InitiativeHierarchyContext)
   const initiativeBody = initiative?.body?.elements?.[0]?.body || ''

   if (initiativeLoading) {
      return (
         <View
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="100%"
            textAlign="center"
            padding="90px"
            borderRadius="6px">
            <Spinner color={theme.tmpContentTextColor} width="24px" height="24px" />
         </View>
      )
   }

   return (
      <>
         <Helmet title={`${initiative.name} | ${organization.name}`} />
         <View width="100%">
            <View
               position="relative"
               float="left"
               width="100%"
               height={theme.tmpViewportHeight}
               borderLeft={`1px solid ${theme.tmpBorderColor}`}
               border22={`1px solid ${theme.tmpBorderColor}`}>
               <View
                  position="relative"
                  float="left"
                  width22="100%[a-b] 300px[c] 330px[d] 370px[e-f]"
                  width="280px[a-d]  24vw[e] 22vw[f]"
                  height="100%"
                  paddingBottom="30px"
                  paddingHorizontal={theme.tmpSpacing}
                  scroll="on">
                  <View
                     display="flex"
                     alignItems="center"
                     width="100%"
                     height={theme.tmpTitlebarHeight}
                     maxHeight={theme.tmpTitlebarMaxHeight}
                     minHeight={theme.tmpTitlebarMinHeight}
                     paddingLeft="5px"
                     borderBottom={`1px solid ${theme.tmpBorderColor}`}>
                     <Text
                        color={theme.tmpTitleColor}
                        size={theme.tmpTitleMdSize}>
                        {initiative.name}
                     </Text>
                  </View>
                  {initiativeBody && (
                     <View
                        float="left"
                        width="100%"
                        paddingVertical={theme.tmpSpacing}>
                        <Text
                           size="2[a-e] 2.5[f]"
                           lineHeight="150%"
                           color={theme.tmpButtonTextColor}>
                           <RichText html={initiativeBody} />
                           <br />
                        </Text>
                     </View>
                  )}
                  <View
                     paddingVertical={theme.tmpSpacing}
                     borderBottom={`1px solid ${theme.tmpBorderColor}`}>
                     <ListMenuButton
                        linkExact
                        icon="[ ]"
                        linkTo={`${match.url}/messages/${initiative.thread.id}`}
                        name="Messages"
                        {...buttonStyleProps}
                        borderStyle={!initiativeBody ? 'none' : undefined}
                     />
                     <ListMenuButton
                        linkExact
                        icon="[ ]"
                        linkTo={`${match.url}/people`}
                        name="People"
                        {...buttonStyleProps}
                     />
                     <ListMenuButton
                        icon="[ ]"
                        linkTo={`${match.url}/related/event`}
                        name="Events"
                        {...buttonStyleProps}
                     />
                     <ListMenuButton
                        icon="[ ]"
                        linkTo={`${match.url}/related/resource`}
                        name="Resources"
                        {...buttonStyleProps}
                     />
                  </View>
                  <PeopleBlock />
                  <View
                     position="absolute"
                     bottom="0px"
                     left={theme.tmpSpacing}
                     right={theme.tmpSpacing}
                     paddingVertical={theme.tmpSpacing}>
                     <View display="flex" width="100%">
                        <View flex="1 1 auto">
                           <InitiativeJoinButton
                              buttonSize="lg"
                              buttonWidth="100%"
                              popoverAnchorOriginVertical="bottom"
                           />
                        </View>
                        <InitiativeMoreButton
                           buttonSize="lg"
                           color={buttonColor}
                           iconColor="#fff"
                        />
                     </View>
                  </View>
               </View>
               <Switch>
                  <Redirect exact from={match.path} to={`${match.path}/messages`} />
                  <Route
                     path={[
                        `${match.url}/messages`,
                        `${match.url}/people`,
                        `${match.url}/related`
                     ]}>
                     <View
                        position="absolute"
                        top="0px"
                        left22="0px[a-b] 300px[c] 330px[d] 370px[e-f]"
                        left="280px[a-d]  24vw[e] 22vw[f]"
                        right="0px"
                        height="100%"
                        paddingLeft={theme.tmpSpacing}
                        backgroundColor={theme.tmpContentBackgroundColor}
                        borderLeft={`1px solid ${theme.tmpBorderColor}`}
                        borderRight={`1px solid ${theme.tmpBorderColor}[f]`}
                        zIndex="3">
                        <Switch>
                           <Route
                              path={`${match.path}/messages/:threadId`}
                              component={ThreadChatView}
                           />
                           <Route path={`${match.path}/people`} component={People} />
                           <Route path={`${match.path}/related/:relatedClass`} component={Related} />
                        </Switch>
                     </View>
                  </Route>
                  {initiative.currentUserCanEdit && (
                     <ModalRoute path={`${match.path}/settings`}>
                        {({ open, onCloseComplete, onCloseTrigger }) => (
                           <Modal
                              borderRadius="3px"
                              overlayBackgroundColor="rgba(45,40,40,0.9)"
                              height="100%[a-b] 85%[c-f]"
                              width="100%[a-b] 90%[c-d] 1200px[e-f]"
                              onCloseComplete={onCloseComplete}
                              onCloseTrigger={onCloseTrigger}
                              open={open}>
                              <Settings returnUrl={match.url} />
                           </Modal>
                        )}
                     </ModalRoute>
                  )}
               </Switch>
            </View>
         </View>
      </>
   )
}

export default withInitiativeHierarchyProvider({ slugParam: 'initiativeSlug' })(GroupView)
